import { Global } from "@emotion/core";
import { ThemeKey, commonTokens } from "@smartsheet/design-tokens";
import { HTMLAttributes, useEffect, useLayoutEffect, useRef, useState } from "react";
import React from "react";
import { BODY_MARKER_ATTRIBUTE } from "./ThemeMarkerAttribute";
import { createTokenInjector } from "./createTokenInjector";

const BACKGROUND_COLOR_TRANSITION_MILLISECONDS = 200;

const { getToken, Injector } = createTokenInjector({
    tokens: commonTokens,
    supportedThemes: ["lodestar-light", "ursa-light", "ursa-dark"],
    namespace: "com",
});

/**
 * @deprecated This utility is being removed in V6+, February 2025, in favor of:
 * - `SDSThemeProvider` from `@smartsheet/lodestar-core`
 * - a global tokens stylesheet from `@smartsheet/design-tokens`
 *
 * Note that the `createTokenInjector()` function will still be available until further notice,
 * enabling teams to continue injecting and accessing their own sets of tokens as needed.
 */
export interface CommonTokenInjectorProps extends HTMLAttributes<HTMLElement> {
    themeKey: ThemeKey;
}

/**
 * @deprecated This utility is being removed in V6+, February 2025, in favor of:
 * - `SDSThemeProvider` from `@smartsheet/lodestar-core`
 * - a global tokens stylesheet from `@smartsheet/design-tokens`
 *
 * Note that the `createTokenInjector()` function will still be available until further notice,
 * enabling teams to continue injecting and accessing their own sets of tokens as needed.
 */
export const CommonTokenInjector = ({ themeKey }: CommonTokenInjectorProps) => {
    const [_actualThemeKey, setActualThemeKey] = useState(themeKey);
    const [applyBackgroundColorTransition, setApplyBackgroundColorTransition] = useState(false);

    // Users should get the right theme immediately. This helps w/ tests and the initial paint.
    const firstMountRef = useRef(true);
    if (firstMountRef.current) {
        document.body.setAttribute(BODY_MARKER_ATTRIBUTE, themeKey);
        firstMountRef.current = false;
    }

    useLayoutEffect(() => {
        let animationTimeoutId = 0;
        setApplyBackgroundColorTransition(true);
        // We want to apply the CSS change after a tick to ensure all the React stuff has settled
        // first.
        const rafId = requestAnimationFrame(() => {
            setActualThemeKey(themeKey);
            animationTimeoutId = window.setTimeout(() => {
                setApplyBackgroundColorTransition(false);
            }, BACKGROUND_COLOR_TRANSITION_MILLISECONDS);

            document.body.setAttribute(BODY_MARKER_ATTRIBUTE, themeKey);
        });
        return () => {
            cancelAnimationFrame(rafId);
            clearTimeout(animationTimeoutId);
        };
    }, [themeKey]);

    // When this component is unmounted, we should remove the attribute to clean up.
    useEffect(
        () => () => {
            document.body.removeAttribute(BODY_MARKER_ATTRIBUTE);
        },
        [themeKey]
    );

    return (
        <>
            <Injector themeKey={themeKey} />
            <Global
                styles={{
                    // This should only be added when the themeKey is transitioning or
                    // it affects everything else pretty badly.
                    "*, *:before, *.after": applyBackgroundColorTransition
                        ? {
                              transition: `
                    background-color ${BACKGROUND_COLOR_TRANSITION_MILLISECONDS}ms linear,
                    border-color ${BACKGROUND_COLOR_TRANSITION_MILLISECONDS}ms linear,
                    color ${BACKGROUND_COLOR_TRANSITION_MILLISECONDS}ms linear
                    `,
                          }
                        : {},
                }}
            />
        </>
    );
};

export const color = getToken;
export type SemanticTokenNamesType = Parameters<typeof color>[0];
